import { memo, useState, type ReactNode } from "react";
import {
  Check, ChevronRight, CircleAlert, CircleCheck, Circle, CircleDashed, CircleX, Copy, Inbox, ListChecks, MessageSquareText,
  Share, Sparkle, TriangleAlert, Users, X,
} from "lucide-react";
import {
  ProcessInfo, parseMessageMedia, plainMessageText, subagentFinished, type ChatItem, type MediaRef, type MessageSegment,
  type ProcessGroup, type SubagentRun, type TodoEntry, type ToolCall, type UsageStats,
} from "@timibot/core";
import { useApp, useTicker } from "../../context";
import { copyText } from "../../ui/clipboard";
import { Spinner } from "../../ui/components";
import { TimiMascot } from "../../ui/Mascot";
import { Markdown, markdownToPlain } from "./Markdown";
import { MediaGallery } from "./Media";
import { formatDuration, toolIcon, toolTitle } from "./names";
import "./messages.css";

// MARK: 解析缓存：流式输出时每 60ms 重绘一次，同一段文字不用重复跑正则

const segmentCache = new Map<string, MessageSegment[]>();

export function segmentsOf(text: string, streaming = false): MessageSegment[] {
  let source = text;
  // 流式中最后一行可能是写了一半的 MEDIA:/路径，等这一行写完再解析
  if (streaming) {
    const lastBreak = text.lastIndexOf("\n");
    if (lastBreak >= 0) {
      const tail = text.slice(lastBreak + 1);
      if (/MEDIA:|@image:|!\[|data:image/.test(tail)) source = text.slice(0, lastBreak);
    } else if (text.includes("MEDIA:") || text.startsWith("![")) {
      source = "";
    }
  }
  const hit = segmentCache.get(source);
  if (hit) return hit;
  const parsed = parseMessageMedia(source);
  if (segmentCache.size > 2000) segmentCache.clear();
  segmentCache.set(source, parsed);
  return parsed;
}

export function mediaOf(text: string): MediaRef[] {
  return segmentsOf(text).flatMap((s) => ("media" in s ? s.media : []));
}

export function plainOf(text: string): string {
  return segmentsOf(text).flatMap((s) => ("text" in s ? [s.text] : [])).join("\n\n");
}

/** 消息文字 + 其中的图片/视频，按原顺序排列 */
export function RichText({ text, streaming, small }: { text: string; streaming?: boolean; small?: boolean }) {
  const segments = segmentsOf(text, streaming);
  return (
    <div className="rich">
      {segments.map((seg, i) => ("text" in seg
        ? <Markdown key={i} text={seg.text} small={small} />
        : <MediaGallery key={i} refs={seg.media} />))}
    </div>
  );
}

// MARK: 一行

/** 对话流中的一项（对应 iOS 的 MessageRow）。 */
export const MessageRow = memo(function MessageRow({ item, mediaShownElsewhere, inProcess, usage }: {
  item: ChatItem; mediaShownElsewhere?: Set<string>; inProcess?: boolean; usage?: UsageStats;
}) {
  const k = item.kind;
  switch (k.type) {
    case "user": return <UserBubble text={k.text} />;
    case "assistant":
      return inProcess
        ? <div className="process-aside"><RichText text={k.text} streaming={k.streaming} small /></div>
        : <AssistantMessage text={k.text} streaming={k.streaming} usage={usage} />;
    case "reasoning": return <ReasoningView text={k.text} streaming={k.streaming} startedAt={item.timestamp} duration={item.duration} />;
    case "tool": return <ToolCallRow call={k.call} media={inProcess ? [] : k.call.media.filter((m) => !mediaShownElsewhere?.has(m.source))} />;
    case "todo": return <TodoCard todos={k.todos} />;
    case "subagent": return <SubagentCard run={k.run} />;
    case "sideResult": return <SideResultCard kind={k.kind} question={k.question} text={k.text} />;
    case "notice": return <NoticeRow text={k.text} level={k.level} />;
  }
});

function UserBubble({ text }: { text: string }) {
  // 发送的图片在历史里是 `@image:/服务器路径`，显示成缩略图
  const refs = mediaOf(text);
  const caption = refs.length ? plainOf(text) : text;
  return (
    <div className="user-row">
      {refs.length > 0 && <MediaGallery refs={refs} align="end" />}
      {caption && <div className="user-bubble">{caption}</div>}
    </div>
  );
}

function AssistantMessage({ text, streaming, usage }: { text: string; streaming: boolean; usage?: UsageStats }) {
  const app = useApp();
  const plain = plainMessageText(text);
  const [showUsage, setShowUsage] = useState(false);
  const percent = usage?.context_percent && usage.context_percent > 0 ? Math.min(usage.context_percent, 100) : undefined;
  const share = async () => {
    const body = markdownToPlain(plain);
    if (navigator.share) { try { await navigator.share({ text: body }); return; } catch { return; } }
    await copyText(body);
    app.showToast("已复制");
  };
  return (
    <div className="assistant">
      <RichText text={text} streaming={streaming} />
      {streaming ? <span className="typing-cursor" /> : plain && (
        <div className="assistant-actions t-footnote t-3">
          <button aria-label="复制" title="复制" onClick={async () => { await copyText(markdownToPlain(plain)); app.showToast("已复制"); }}><Copy size={15} /></button>
          <button aria-label="分享" title="分享" onClick={share}><Share size={15} /></button>
          <span className="spacer" />
          {percent !== undefined && (
            <span className="usage-anchor">
              <button onClick={() => setShowUsage((v) => !v)}><ContextMeter percent={percent} /></button>
              {showUsage && <UsagePopover usage={usage} percent={percent} onClose={() => setShowUsage(false)} />}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** “上下文 23%”：小圆环 + 文字，点开看明细。超过 80% 变橙色（快要自动压缩了） */
export function ContextMeter({ percent }: { percent: number }) {
  const warn = percent >= 80;
  const r = 4.5;
  const c = 2 * Math.PI * r;
  return (
    <span className="context-meter t-caption" style={{ color: warn ? "var(--warning)" : "var(--text-3)" }}>
      <svg width="11" height="11" viewBox="0 0 11 11">
        <circle cx="5.5" cy="5.5" r={r} fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
        <circle cx="5.5" cy="5.5" r={r} fill="none" stroke={warn ? "var(--warning)" : "var(--text-2)"} strokeWidth="2"
          strokeLinecap="round" strokeDasharray={`${(c * percent) / 100} ${c}`} transform="rotate(-90 5.5 5.5)" />
      </svg>
      上下文 {percent}%
    </span>
  );
}

function UsagePopover({ usage, percent, onClose }: { usage?: UsageStats; percent: number; onClose: () => void }) {
  const fmt = (n: number) => n.toLocaleString("zh-CN");
  const rows: [string, string][] = [];
  if (usage?.context_used !== undefined && usage.context_max !== undefined) rows.push(["已用 / 上限", `${fmt(usage.context_used)} / ${fmt(usage.context_max)} tokens`]);
  if (usage?.total !== undefined) rows.push(["本对话累计", `${fmt(usage.total)} tokens`]);
  if (usage?.calls !== undefined) rows.push(["模型调用", `${usage.calls} 次`]);
  if (usage?.cost_usd && usage.cost_usd > 0) rows.push(["估算费用", `$${usage.cost_usd.toFixed(4)}`]);
  return (
    <>
      <div className="popover-catcher" onClick={onClose} />
      <div className="usage-popover">
        <div className="t-headline">上下文用量 {percent}%</div>
        {rows.map(([k, v]) => <div key={k} className="usage-row t-footnote"><span className="t-2">{k}</span><span>{v}</span></div>)}
      </div>
    </>
  );
}

/** 进行中的状态胶囊：思考、处理过程、等待回复都用它。进行中左边是迷你 Timi 在转眼珠，结束后换成一个小图标。 */
export function ProgressCapsule({ live, doneIcon, expanded, children, onClick }: {
  live: boolean; doneIcon?: ReactNode; expanded?: boolean; children: ReactNode; onClick?: () => void;
}) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className="progress-capsule t-footnote medium" onClick={onClick} aria-expanded={expanded}>
      {live ? <TimiMascot mood="thinking" size={16} interactive={false} /> : (doneIcon ?? <Sparkle size={12} />)}
      {children}
      {expanded !== undefined && <ChevronRight size={11} strokeWidth={3} className={`capsule-chevron${expanded ? " open" : ""}`} />}
    </Tag>
  );
}

/** 思考过程：默认收成一枚小胶囊。进行中“思考中 · 6 秒”带流光，下面淡淡滚动最后两行；结束后“已思考 12 秒 ›” */
function ReasoningView({ text, streaming, startedAt, duration }: { text: string; streaming: boolean; startedAt?: number; duration?: number }) {
  const [expanded, setExpanded] = useState(false);
  const now = useTicker(streaming);
  const elapsed = startedAt ? Math.floor((now - startedAt) / 1000) : 0;
  const done = duration && duration >= 1
    ? duration < 60 ? `已思考 ${Math.round(duration)} 秒` : `已思考 ${Math.floor(duration / 60)} 分 ${Math.floor(duration % 60)} 秒`
    : "思考过程";
  const preview = text.trim().slice(-160).replace(/\n/g, " ");
  return (
    <div className="reasoning">
      <ProgressCapsule live={streaming} expanded={expanded} onClick={() => setExpanded((v) => !v)}>
        {streaming ? <span className="shimmer">思考中{elapsed >= 1 ? ` · ${elapsed} 秒` : ""}</span> : <span>{done}</span>}
      </ProgressCapsule>
      {expanded ? <div className="reasoning-text t-footnote t-2">{text}</div>
        : streaming && preview ? <div className="reasoning-preview t-caption t-3">{preview}</div> : null}
    </div>
  );
}

function ToolCallRow({ call, media }: { call: ToolCall; media: MediaRef[] }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = toolIcon(call.name);
  const subtitle = call.summary ?? call.context;
  return (
    <div className="tool-card">
      <button className="tool-head" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <span className="tool-icon"><Icon size={13} /></span>
        <span className="tool-text">
          <span className="t-footnote semibold">{toolTitle(call.name)}</span>
          {subtitle && <span className="t-caption t-2 nowrap">{subtitle}</span>}
        </span>
        {call.duration !== undefined && <span className="t-caption2 t-3 mono">{formatDuration(call.duration)}</span>}
        {call.status === "running" ? <Spinner size={14} />
          : call.status === "done" ? <Check size={14} strokeWidth={3} style={{ color: "var(--success)" }} />
          : <X size={14} strokeWidth={3} style={{ color: "var(--danger)" }} />}
      </button>
      {expanded && (
        <div className="tool-details">
          {call.argsText && <Detail title="参数" body={call.argsText} />}
          {call.inlineDiff && <Detail title="改动" body={call.inlineDiff} />}
          {call.resultText && <Detail title="结果" body={call.resultText.slice(0, 4000)} />}
        </div>
      )}
      {media.length > 0 && <div className="tool-media"><MediaGallery refs={media} /></div>}
    </div>
  );
}

function Detail({ title, body }: { title: string; body: string }) {
  return (
    <div className="tool-detail">
      <div className="t-caption2 semibold t-3">{title}</div>
      <pre className="t-caption mono">{body}</pre>
    </div>
  );
}

function TodoCard({ todos }: { todos: TodoEntry[] }) {
  const done = todos.filter((t) => t.state === "completed").length;
  const icon = (s: TodoEntry["state"]) => s === "completed" ? <CircleCheck size={14} style={{ color: "var(--success)" }} />
    : s === "in_progress" ? <CircleDashed size={14} style={{ color: "var(--brand)" }} />
    : s === "cancelled" ? <CircleX size={14} className="t-3" /> : <Circle size={14} className="t-3" />;
  return (
    <div className="card todo-card">
      <div className="row"><ListChecks size={15} /><span className="t-footnote semibold" style={{ marginLeft: 6 }}>任务清单</span><span className="spacer" /><span className="t-caption t-2 mono">{done}/{todos.length}</span></div>
      {todos.map((t) => (
        <div key={t.id} className="todo-row t-footnote">
          {icon(t.state)}
          <span className={t.state === "completed" || t.state === "cancelled" ? "todo-done" : ""}>{t.content}</span>
        </div>
      ))}
    </div>
  );
}

function SubagentCard({ run }: { run: SubagentRun }) {
  const finished = subagentFinished(run);
  const status = ({ queued: "排队中", running: "子 agent 执行中", completed: "已完成", interrupted: "已中断", timeout: "超时" } as Record<string, string>)[run.status] ?? run.status;
  return (
    <div className="card subagent-card">
      <Users size={15} style={{ color: "var(--brand)", flexShrink: 0, marginTop: 2 }} />
      <div className="col" style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <span className="t-footnote semibold clamp2">{run.goal}</span>
        <span className="t-caption t-2 subagent-meta">
          <span style={{ color: finished ? undefined : "var(--brand)" }}>{status}</span>
          {run.toolCount ? <span>{run.toolCount} 次工具调用</span> : null}
          {!finished && run.lastTool ? <span className="nowrap">{run.lastTool}</span> : null}
          {run.duration !== undefined ? <span>{formatDuration(run.duration)}</span> : null}
        </span>
        {run.summary && <span className="t-caption t-2 clamp4">{run.summary}</span>}
      </div>
      {!finished && <Spinner size={14} />}
    </div>
  );
}

function SideResultCard({ kind, question, text }: { kind: string; question?: string; text: string }) {
  return (
    <div className="card side-card">
      <span className="t-footnote semibold t-brand row" style={{ gap: 6 }}>
        {kind === "btw" ? <MessageSquareText size={14} /> : <Inbox size={14} />}
        {kind === "btw" ? "旁支回答" : "后台任务完成"}
      </span>
      {question && <span className="t-caption t-2">{question}</span>}
      <RichText text={text} small />
    </div>
  );
}

function NoticeRow({ text, level }: { text: string; level: "info" | "warning" | "error" }) {
  return (
    <div className={`notice ${level} t-footnote`}>
      {level === "error" ? <TriangleAlert size={13} /> : level === "warning" ? <CircleAlert size={13} /> : null}
      <span>{text}</span>
    </div>
  );
}

// MARK: 过程折叠

/**
 * 一轮里的“处理过程”：思考、工具、子任务、中途旁白收成一行（对应 iOS 的 ProcessGroupView）。
 * - 进行中：“正在处理 · 第 N 步”，下面只露出最新一步
 * - 完成后：折叠成“思考 8 秒 · 3 个步骤 ›”，点开看全部步骤
 * - 工具生成的图片/视频不折叠
 */
export function ProcessGroupView({ group, expanded, onToggle, mediaShownElsewhere }: {
  group: ProcessGroup; expanded: boolean; onToggle: () => void; mediaShownElsewhere: Set<string>;
}) {
  const now = useTicker(group.isLive);
  const steps = group.items.length;
  const started = group.items[0]?.timestamp;
  const s = started ? Math.floor((now - started) / 1000) : undefined;
  const liveTitle = s !== undefined
    ? (steps > 1 ? `正在处理 · 第 ${steps} 步 · ${s} 秒` : `正在处理 · ${s} 秒`)
    : (steps > 1 ? `正在处理 · 第 ${steps} 步` : "正在处理");
  const parts: string[] = [];
  const thinking = ProcessInfo.thinkingSeconds(group);
  const tools = ProcessInfo.toolCount(group);
  if (thinking !== undefined && thinking >= 1) parts.push(thinking < 60 ? `思考 ${Math.round(thinking)} 秒` : `思考 ${Math.floor(thinking / 60)} 分钟`);
  else if (ProcessInfo.hasReasoning(group) && tools === 0) parts.push("思考过程");
  if (tools > 0) parts.push(`${tools} 个步骤`);
  const subs = ProcessInfo.subagentCount(group);
  if (subs > 0) parts.push(`${subs} 个帮手`);
  const failed = ProcessInfo.failedCount(group);
  if (failed > 0) parts.push(`${failed} 个失败`);
  const doneTitle = parts.length ? parts.join(" · ") : "处理过程";
  const latest = group.items[group.items.length - 1];
  const media = expanded ? [] : ProcessInfo.media(group).filter((m) => !mediaShownElsewhere.has(m.source));

  return (
    <div className="process">
      <ProgressCapsule live={group.isLive} doneIcon={<CircleCheck size={12} />} expanded={expanded} onClick={onToggle}>
        {group.isLive ? <span className="shimmer">{liveTitle}</span> : (
          <>
            <span>{doneTitle}</span>
            {ProcessInfo.toolNames(group).length > 0 && (
              <span className="capsule-icons t-3">
                {ProcessInfo.toolNames(group).slice(0, 4).map((n) => { const I = toolIcon(n); return <I key={n} size={11} />; })}
              </span>
            )}
          </>
        )}
      </ProgressCapsule>
      {expanded ? (
        <div className="process-items">
          {group.items.map((item) => <MessageRow key={item.id} item={item} mediaShownElsewhere={mediaShownElsewhere} inProcess />)}
        </div>
      ) : group.isLive && latest ? <LiveStepLine item={latest} /> : null}
      {media.length > 0 && <MediaGallery refs={media} />}
    </div>
  );
}

/** 进行中的“处理过程”下面那一行：最新一步在做什么 */
function LiveStepLine({ item }: { item: ChatItem }) {
  const k = item.kind;
  const tail = (t: string) => t.trim().slice(-120).replace(/\n/g, " ");
  let content: ReactNode = null;
  if (k.type === "reasoning") content = <span className="clamp2">{tail(k.text)}</span>;
  else if (k.type === "tool") {
    const Icon = toolIcon(k.call.name);
    content = <span className="row nowrap" style={{ gap: 5 }}><Icon size={12} />{[toolTitle(k.call.name), k.call.context].filter(Boolean).join(" · ")}</span>;
  } else if (k.type === "assistant") content = <span className="clamp2">{tail(plainOf(k.text))}</span>;
  else if (k.type === "subagent") content = <span className="row nowrap" style={{ gap: 5 }}><Users size={12} />{k.run.goal}</span>;
  else if (k.type === "todo") content = <span className="row" style={{ gap: 5 }}><ListChecks size={12} />任务清单 {k.todos.filter((t) => t.state === "completed").length}/{k.todos.length}</span>;
  return content ? <div className="live-step t-caption t-3">{content}</div> : null;
}

/** 发出消息后、还没有任何输出时的占位：和思考胶囊同一个样子 */
export function WaitingRow({ status }: { status?: string }) {
  const title = !status || status === "等待回复…" ? "思考中" : status;
  return <ProgressCapsule live><span className="shimmer">{title}</span></ProgressCapsule>;
}
